import {
    ANCHOR_CHAINS,
    CRONOS_CHAIN_ID,
    ROBINHOOD_CHAIN_ID,
    type AnchorChainId,
} from '@/features/wallet/config/anchors';
import {
    bridgeNameSchema,
    transferFeesSchema,
    USDC_DECIMALS,
} from '@/lib/api/speed';
import type {
    BridgeCatalog,
    BridgeLeg,
    BridgeName,
    BridgeQuoteResponse,
    SpeedJobResponse,
} from '@/lib/api/speed';
import { isPlainUserSentence } from '@/lib/errors';

/**
 * What the backend says a transfer's bridge leg costs, and which bridge it took.
 *
 * The balance endpoints advertise a FLAT estimate for the user's next
 * withdrawal. That number is right while Relay carries the route (about $0.02)
 * and badly wrong while the deBridge failover does (dollars, because DLN
 * charges a fixed native fee per order). So once a transfer exists, the figure
 * it reports for its OWN route wins over the advertised estimate, and the UI
 * names deBridge rather than quietly charging for it.
 *
 * Everything here is opportunistic: a backend that predates the failover sends
 * none of it, and the UI then behaves exactly as it did before.
 */

/**
 * Name to show for each bridge when no catalog is at hand (fee lines read off a
 * job or quote). The catalog carries the backend's own labels; see bridgeLabel.
 * Not an id; ids live in serviceWalletRegistry.ts.
 */
export const BRIDGE_LABELS: Record<BridgeName, string> = {
    relay: 'Relay',
    lifi: 'LI.FI',
    debridge: 'deBridge',
};

/** Every bridge this app knows, in the order the backend usually tries them. */
const ALL_BRIDGES = Object.keys(BRIDGE_LABELS) as BridgeName[];

/** The backend's own label for a bridge when the catalog is loaded, else the local one. */
export function bridgeLabel(
    catalog: BridgeCatalog | null | undefined,
    name: BridgeName,
): string {
    return (
        catalog?.bridges.find((bridge) => bridge.name === name)?.label ??
        BRIDGE_LABELS[name]
    );
}

/**
 * The bridges that may carry a leg between two chains, in the order the
 * backend tries them: the ones enabled on BOTH chains in the registry, in the
 * origin chain's order, filtered to the bridges that can carry the leg at all.
 * Mirrors the backend's own selection so the testing switch offers exactly
 * what a pin can actually run.
 *
 * Without a catalog (not loaded yet, or an older backend without the route)
 * every bridge this app knows is offered, and the backend refuses a pin it
 * cannot honour.
 */
export function bridgeOptionsFor(
    catalog: BridgeCatalog | null | undefined,
    originChainId: number,
    destinationChainId: number,
    leg: BridgeLeg,
): BridgeName[] {
    if (!catalog) {
        return ALL_BRIDGES;
    }
    const origin = catalog.chains[String(originChainId)] ?? [];
    const destination = catalog.chains[String(destinationChainId)] ?? [];
    return origin.filter(
        (name) =>
            destination.includes(name) &&
            (catalog.bridges
                .find((bridge) => bridge.name === name)
                ?.legs.includes(leg) ??
                false),
    );
}

export interface TransferBridgeFees {
    /** Which bridge carried it, when the backend named one. */
    bridge: BridgeName | null;
    /** Its cost in USD, when the backend quoted one. */
    bridgeFeeUsd: number | null;
}

export interface DepositBridgeInfo extends TransferBridgeFees {
    /** What the quote expects to land, in display units. */
    estimatedReceive: number | null;
}

/** Decimal USD strings ('2.20'), never base units. Junk reads as "not stated". */
function toUsdNumber(raw: unknown): number | null {
    if (typeof raw !== 'string' || raw.trim() === '') return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

/** Token units, 6dp base units on the wire (`estimatedReceive`). */
function toTokenNumber(raw: unknown): number | null {
    if (typeof raw !== 'string' || !/^\d+$/u.test(raw)) return null;
    const parsed = Number(raw) / 10 ** USDC_DECIMALS;
    return Number.isFinite(parsed) ? parsed : null;
}

function toBridgeName(raw: unknown): BridgeName | null {
    const parsed = bridgeNameSchema.safeParse(raw);
    return parsed.success ? parsed.data : null;
}

/**
 * A wire `fees` object read into display numbers. Anything unreadable becomes
 * null rather than an error: this is a figure the UI shows, never one anything
 * depends on, and a fee we cannot parse must not break a withdrawal.
 *
 * Used for both surfaces the number arrives on: the fund-authorization
 * challenge (before the user commits) and the job/transfer payload (after).
 */
export function normalizeTransferFees(raw: unknown): TransferBridgeFees {
    const parsed = transferFeesSchema.safeParse(raw);
    const fees = parsed.success ? parsed.data : {};
    return {
        bridge: toBridgeName(fees.bridge),
        bridgeFeeUsd: toUsdNumber(fees.bridgeFeeUsd),
    };
}

/**
 * The fee figures a job carries. The wire puts them under `fees`; the deposit
 * start payload also carries `bridge`/`bridgeFeeUsd` at the top level, so both
 * are read and `fees` wins.
 */
export function bridgeFeesFromJob(job: SpeedJobResponse): TransferBridgeFees {
    const response = (job.response ?? {}) as Record<string, unknown>;
    const fees = normalizeTransferFees(response.fees);
    return {
        bridge: fees.bridge ?? toBridgeName(response.bridge),
        bridgeFeeUsd: fees.bridgeFeeUsd ?? toUsdNumber(response.bridgeFeeUsd),
    };
}

/** The same, plus the deposit start payload's `estimatedReceive`. */
export function depositBridgeInfoFromJob(
    job: SpeedJobResponse,
): DepositBridgeInfo {
    const response = (job.response ?? {}) as Record<string, unknown>;
    return {
        ...bridgeFeesFromJob(job),
        estimatedReceive: toTokenNumber(response.estimatedReceive),
    };
}

/**
 * A display pre-quote, read into numbers the UI can render.
 *
 * `ok` is the only member that decides anything: it is what unlocks the button.
 * Everything else is display, so anything unreadable becomes null and the
 * surface says less rather than refusing to work.
 */
export interface BridgeQuote {
    /** The backend would accept this amount over this route. */
    ok: boolean;
    bridge: BridgeName | null;
    /** USD. */
    bridgeFeeUsd: number | null;
    /** Display units of the destination token. */
    estimatedReceive: number | null;
    /** USD floor for THIS route, which the deBridge failover raises. */
    minimumUsd: number | null;
    /** Plain backend copy for an `ok: false`, rendered verbatim. */
    reason: string | null;
    /** Machine name of the refusal, when the backend gave one. */
    code: string | null;
}

/** Backend copy is shown verbatim, so it has to LOOK like copy. */
function toReasonText(raw: unknown): string | null {
    if (typeof raw !== 'string') return null;
    const trimmed = raw.trim();
    return isPlainUserSentence(trimmed) ? trimmed : null;
}

export function normalizeBridgeQuote(raw: BridgeQuoteResponse): BridgeQuote {
    return {
        ok: raw.ok === true,
        bridge: raw.bridge ?? null,
        bridgeFeeUsd: toUsdNumber(raw.bridgeFeeUsd),
        estimatedReceive: toTokenNumber(raw.estimatedReceive),
        minimumUsd: toUsdNumber(raw.minimumUsd),
        reason: toReasonText(raw.reason),
        code: typeof raw.code === 'string' && raw.code ? raw.code : null,
    };
}

/**
 * deBridge's fixed native order fee per anchor, the one the user's OWN wallet
 * pays on a metamask-route deposit (it rides the order transaction's `value`).
 * Pinned here because it is a protocol constant the backend does not send in
 * native units, and the deposit warning has to name it BEFORE anything is
 * quoted. Rounded up in the copy, never down: a user told "about 15 CRO" who
 * holds 14 has a failed transaction, which is the whole point of the warning.
 */
export const DEBRIDGE_NATIVE_FEE: Record<AnchorChainId, string> = {
    [CRONOS_CHAIN_ID]: '15 CRO',
    [ROBINHOOD_CHAIN_ID]: '0.001 ETH',
};

/** The same fee as a number in the anchor's native currency, for the balance check. */
export const DEBRIDGE_NATIVE_FEE_AMOUNT: Record<AnchorChainId, number> = {
    [CRONOS_CHAIN_ID]: 15,
    [ROBINHOOD_CHAIN_ID]: 0.001,
};

export interface NativeBridgeFee {
    /** In the anchor's native currency. */
    amount: number;
    symbol: string;
    /** "15 CRO" */
    label: string;
}

/**
 * deBridge's fixed fee on a wallet-signed deposit from this anchor: it is paid
 * in the chain's NATIVE currency (it rides the order transaction's value), not
 * out of the stable being deposited, so the user needs that much on top of gas.
 */
export function debridgeNativeFee(
    anchorChainId: AnchorChainId,
): NativeBridgeFee {
    return {
        amount: DEBRIDGE_NATIVE_FEE_AMOUNT[anchorChainId],
        symbol: ANCHOR_CHAINS[anchorChainId].network.nativeCurrency.symbol,
        label: debridgeNativeFeeLabel(anchorChainId),
    };
}

/** "about 15 CRO", for the anchor a deposit originates on. */
export function debridgeNativeFeeLabel(anchorChainId: AnchorChainId): string {
    return (
        DEBRIDGE_NATIVE_FEE[anchorChainId] ??
        `${ANCHOR_CHAINS[anchorChainId]?.label ?? ''} network fee`
    );
}
